import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { createCartSnapshot, readCartSnapshot } from "./cartRecovery";
import { validateRecoveredCart } from "../../actions/saleAction/cartRecoveryAction";

export default function useCartRecovery({ storageKey, enabled, formState, isDiscount, onRestore, onReset }) {
  const [snapshot, setSnapshot] = useState(null);
  const [readyKey, setReadyKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const blocked = useRef(true);
  const submitting = useRef(false);
  const cleared = useRef(false);
  const warned = useRef(false);
  const identity = useRef(null);
  const callbacks = useRef({ onRestore, onReset });
  callbacks.current = { onRestore, onReset };
  const writer = useRef(null);
  if (!writer.current) writer.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const storageError = useCallback(() => {
    if (!warned.current) {
      warned.current = true;
      toast.error("Keranjang tidak bisa disimpan otomatis. Gunakan Simpan Draft agar tidak hilang.");
    }
  }, []);

  useLayoutEffect(() => {
    blocked.current = true;
    setReadyKey(null);
    setSnapshot(null);
    setWarnings([]);
    if (!enabled || !storageKey) return;
    if (identity.current && identity.current !== storageKey) callbacks.current.onReset();
    identity.current = storageKey;
    try {
      const saved = readCartSnapshot(storageKey);
      setSnapshot(saved);
      blocked.current = Boolean(saved);
    } catch { storageError(); }
    setReadyKey(storageKey);
  }, [storageKey, enabled, storageError]);

  useLayoutEffect(() => {
    if (!enabled || !storageKey || readyKey !== storageKey || blocked.current || submitting.current || cleared.current) return;
    try {
      if (!formState.details?.length) window.localStorage.removeItem(storageKey);
      else window.localStorage.setItem(storageKey, JSON.stringify({ ...createCartSnapshot(formState, isDiscount), writer: writer.current }));
    } catch { storageError(); }
  }, [formState, isDiscount, storageKey, readyKey, enabled, storageError]);

  useLayoutEffect(() => {
    if (!formState.details?.length) cleared.current = false;
  }, [formState.details]);

  useLayoutEffect(() => {
    const onStorage = (event) => {
      if (!enabled || event.key !== storageKey) return;
      // Jangan diam-diam menimpa keranjang yang sedang digunakan tab lain.
      blocked.current = true;
      setSnapshot({ externalChange: true, details: [] });
      setError("Keranjang berubah di tab lain. Gunakan satu tab POS untuk transaksi ini.");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [enabled, storageKey]);

  const clear = () => {
    cleared.current = true;
    submitting.current = false;
    setWarnings([]);
    try { if (storageKey) window.localStorage.removeItem(storageKey); } catch { storageError(); }
  };

  const discard = () => {
    if (busy || snapshot?.externalChange) return;
    clear();
    blocked.current = false;
    setSnapshot(null);
    setError("");
    callbacks.current.onReset();
  };

  const restore = async () => {
    if (busy || !snapshot || snapshot.pendingSubmission || snapshot.externalChange) return;
    setBusy(true);
    setError("");
    try {
      const result = await validateRecoveredCart(snapshot);
      if (identity.current !== storageKey || readCartSnapshot(storageKey)?.savedAt !== snapshot.savedAt) {
        throw new Error("Keranjang sudah berubah. Muat ulang halaman dulu.");
      }
      callbacks.current.onRestore(snapshot, result);
      blocked.current = false;
      cleared.current = false;
      setWarnings(result.warnings);
      setSnapshot(null);
      toast.success("Keranjang sudah kembali. Cek dulu sebelum bayar.");
    } catch (err) {
      setError(err?.userMessage || err?.response?.data?.message || (err?.isAxiosError
        ? "Keranjang belum bisa dibuka. Periksa koneksi lalu coba lagi."
        : err.message) || "Keranjang belum bisa dibuka. Periksa koneksi lalu coba lagi.");
    } finally { setBusy(false); }
  };

  const markSubmitting = () => {
    submitting.current = true;
    try {
      if (storageKey && enabled) window.localStorage.setItem(storageKey, JSON.stringify({
        ...createCartSnapshot(formState, isDiscount), pendingSubmission: true, writer: writer.current,
      }));
    } catch { storageError(); }
  };

  const finishSubmission = (result) => {
    if (result?.success) clear();
    else if ([400, 422].includes(result?.error?.response?.status)) {
      submitting.current = false;
      try {
        if (storageKey) window.localStorage.setItem(storageKey, JSON.stringify({ ...createCartSnapshot(formState, isDiscount), writer: writer.current }));
      } catch { storageError(); }
    }
    // Timeout/putus koneksi: simpan penanda ambigu sampai kasir memeriksa Penjualan.
  };

  return { snapshot, busy, error, warnings, restore, discard, clear, markSubmitting, finishSubmission, dismissWarnings: () => setWarnings([]) };
}
